import React, { useEffect, useRef } from 'react'
import { UseFormRegister, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Combobox,
  ComboboxItem,
  DateErrorType,
  DateType,
  Input,
  SingleDate,
} from '@node-space/ui-components'
import { useDateInput } from 'hooks/forms/useDateInput'
import { scrollToTopError } from 'pages/EmbeddedCustomers/helpers/utils'
import { AddCustomerFormErrors, AddCustomerPayload } from 'types/customers'
import { CustomerAddress } from '../CustomerAddress'

interface IndividualCustomerFieldsProps {
  errors?: AddCustomerFormErrors
  firstName?: string
  lastName?: string
  dateOfBirth?: string
  stateCode?: string
  isLocalBeneficiary?: boolean
  addressLine1?: string
  addressLine2?: string
  city?: string
  postalCode?: string
  countryCode?: string
  countries?: ComboboxItem[]
  isCountriesLoading?: boolean
  states?: ComboboxItem[]
  isFetchingStates?: boolean
  personalIdNumber: string
  individualCustomerDescription?: string
  register?: UseFormRegister<AddCustomerPayload>
  setValue?: (name: string, value: string) => void
  renderCurrencySpecificFields?: () => React.ReactNode
}

export const IndividualCustomerFields = ({
  errors,
  firstName,
  lastName,
  dateOfBirth,
  stateCode,
  addressLine1,
  addressLine2,
  city,
  postalCode,
  countryCode,
  countries,
  isCountriesLoading,
  states,
  isFetchingStates,
  personalIdNumber,
  individualCustomerDescription,
  register,
  setValue,
}: IndividualCustomerFieldsProps) => {
  const { t } = useTranslation()

  const {
    singleDateLabels,
    getDateFields,
    setDateInputValue,
    setDateErrors,
    getDateErrors,
    getDateErrorText,
  } = useDateInput()

  const handleonDateOfBirthChange = (date: DateType) => {
    setValue('dateOfBirth', setDateInputValue(date))
  }

  const handleNationalitySelect = (countryCode: string) => {
    const nationalityName = countries.find(country => country?.value === countryCode)
      ?.leftText as string
    setValue('nationality', countryCode)
    setValue('nationalityName', nationalityName)
  }

  const fieldRefs = useRef<Record<string, HTMLElement | null>>({})

  const onChange = (field: string, value: string) => {
    setValue(field, value)
  }

  const formLike = {
    formState: { errors: errors ?? {} },
  } as unknown as UseFormReturn<Record<string, unknown>>

  useEffect(() => {
    if (!errors) return
    scrollToTopError(formLike, fieldRefs)
  }, [errors])

  return (
    <Box flexCol gapY={16}>
      <Box flex gapX={16}>
        <Box ref={el => (fieldRefs.current.firstName = el)} flexSize="auto" width="full">
          <Input
            {...register('firstName')}
            label={t('formFields.firstName')}
            placeholder={t('enterName')}
            inputMode="text"
            name="firstName"
            testId="first-name"
            errorText={errors?.firstName?.message}
            error={!!errors?.firstName}
            value={firstName}
            onChange={e => setValue('firstName', e?.target?.value)}
          />
        </Box>
        <Box ref={el => (fieldRefs.current.lastName = el)} flexSize="auto" width="full">
          <Input
            {...register('lastName')}
            label={t('formFields.lastName')}
            placeholder={t('enterLastName')}
            inputMode="text"
            name="lastName"
            testId="last-name"
            errorText={errors?.lastName?.message}
            error={!!errors?.lastName}
            value={lastName}
            onChange={e => setValue('lastName', e?.target?.value)}
          />
        </Box>
      </Box>

      <Box ref={el => (fieldRefs.current.personalIdNumber = el)}>
        <Input
          {...register('personalIdNumber')}
          label={t('customers.personalIdNumber')}
          inputMode="text"
          name="personalIdNumber"
          testId="personalIdNumber"
          errorText={errors?.personalIdNumber?.message}
          error={!!errors?.personalIdNumber}
          value={personalIdNumber}
          onChange={e => setValue('personalIdNumber', e?.target?.value)}
        />
      </Box>

      <Box ref={el => (fieldRefs.current.dateOfBirth = el)}>
        <SingleDate
          {...register('dateOfBirth')}
          date={getDateFields(dateOfBirth)}
          labels={{
            ...singleDateLabels,
            errorMessage: getDateErrorText(errors?.dateOfBirth),
          }}
          errors={getDateErrors(errors?.dateOfBirth)}
          onChange={handleonDateOfBirthChange}
          onValidate={(errors: DateErrorType) => setDateErrors(errors)}
        />
      </Box>

      <Box ref={el => (fieldRefs.current.nationality = el)} flexSize="fill">
        <Combobox
          {...register('nationality')}
          items={countries}
          loading={isCountriesLoading}
          label={t('customers.countryOfNationality')}
          searchPlaceholder={t('searchOrSelectACountry')}
          selectPlaceholder={t('searchOrSelectACountry')}
          onChange={handleNationalitySelect}
          id="nationality"
          errorText={errors?.nationality?.message}
          defaultValue={countryCode}
        />
      </Box>

      <Box ref={el => (fieldRefs.current.individualCustomerDescription = el)}>
        <Input
          {...register('individualCustomerDescription')}
          label={t('customers.internalCustomerDescription')}
          inputMode="text"
          name="individual-customer-description"
          testId="individual-customer-description"
          errorText={errors?.individualCustomerDescription?.message}
          error={!!errors?.individualCustomerDescription}
          value={individualCustomerDescription}
          onChange={e => onChange('individualCustomerDescription', e?.target?.value)}
        />
      </Box>

      <Box ref={el => (fieldRefs.current.addressLine1 = el)}>
        <CustomerAddress
          title={t('customers.residentialAddress')}
          errors={errors}
          addressLine1={addressLine1}
          addressLine2={addressLine2}
          city={city}
          postalCode={postalCode}
          countryCode={countryCode}
          countries={countries}
          isCountriesLoading={isCountriesLoading}
          register={register}
          setValue={setValue}
          isIndividual={true}
          stateCode={stateCode}
          states={states}
          isStatesLoading={isFetchingStates}
        />
      </Box>
    </Box>
  )
}
